import { Button, InputNumber } from "@sellar-io/components";
import { CaseSizeRuleUpdateRequestType } from "@sellar-io/api-v2/types";
import { Control, useForm, useWatch } from "react-hook-form";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { generateProductItemName } from "../helpers/generateProductItemName";
import { CustomerBanner } from "../alerts/CustomerBanner";
import { useEffect } from "react";
import { useCaseSizeRuleUpdate } from "../../../hooks/api/v2/caseSizeRule/useCaseSizeRuleUpdate";
import { Alert } from "../../../components/alert/Alert";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useCaseSizeRuleDelete } from "../../../hooks/api/v2/caseSizeRule/useCaseSizeRuleDelete";
import { useSellarSupplier } from "../../../hooks/useSellarSupplier";

type FormInputs = CaseSizeRuleUpdateRequestType;

const CustomerAdviseBanner = ({
  control,
}: {
  control: Control<FormInputs>;
}) => {
  const supplier = useSellarSupplier();
  const { containerType, volume, unit, quantity } = useWatch({
    control,
  });

  const allFieldsHaveValues = containerType && volume && unit && quantity;

  if (!allFieldsHaveValues) {
    return null;
  }

  const productItemName = generateProductItemName({
    containerType,
    volume,
    unit,
  });

  return (
    <div className="my-4">
      <CustomerBanner
        title={`Your customers will be ${supplier.enforceCaseSizeRules ? "required" : "advised"} to order ${productItemName} in quantities of ${quantity}`}
        message={`Please note that the case size you input will be used to calculate any MOQ or volume discounts you have set for this format.`}
      />
    </div>
  );
};

export const CaseSizeRuleUpdateDialog = ({
  onDismiss,
  active,
  caseSizeRule,
}: {
  onDismiss: () => void;
  active: boolean;
  caseSizeRule: CaseSizeRuleUpdateRequestType & { id: number };
}) => {
  const caseSizeRuleDeleteMutation = useCaseSizeRuleDelete(caseSizeRule.id);
  const caseSizeRuleUpdateMutation = useCaseSizeRuleUpdate(caseSizeRule.id);

  const { register, handleSubmit, control, reset } = useForm<FormInputs>({
    shouldUseNativeValidation: true,
    defaultValues: caseSizeRule,
  });

  useEffect(() => {
    if (active) {
      reset(caseSizeRule);
    }
  }, [active, caseSizeRule, reset]);

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <form
        onSubmit={handleSubmit((data) => {
          caseSizeRuleUpdateMutation.mutate(data, {
            onSuccess: () => {
              onDismiss();
            },
          });
        })}
      >
        <DialogNavigation
          title={"Edit case size rule"}
          description={``}
          leftIcon={"CHEVRON_DOWN"}
          onClickLeftIcon={onDismiss}
        />

        <DialogContent>
          {caseSizeRuleUpdateMutation.isError ? (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(caseSizeRuleUpdateMutation.error)?.response
                  ?.data.message ??
                "Unknown error occurred updating case size rule."
              }
            />
          ) : null}

          {caseSizeRuleDeleteMutation.isError ? (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(caseSizeRuleDeleteMutation.error)?.response
                  ?.data.message ??
                "Unknown error occurred deleting case size rule."
              }
            />
          ) : null}

          <InputLabel>Container type</InputLabel>
          <InputContainer>
            <InputSelect {...register("containerType", { required: true })}>
              <InputOption value={"bottle"}>Bottle</InputOption>
              <InputOption value={"can"}>Can</InputOption>
            </InputSelect>
          </InputContainer>

          <InputLabel>Volume</InputLabel>
          <InputContainer>
            <InputNumber
              min={1}
              {...register("volume", {
                required: true,
                valueAsNumber: true,
              })}
            />
          </InputContainer>

          <InputLabel>Unit</InputLabel>
          <InputContainer>
            <InputSelect {...register("unit", { required: true })}>
              <InputOption value={"gal"}>gal - gallon</InputOption>
              <InputOption value={"L"}>L - litre</InputOption>
              <InputOption value={"ml"}>ml - millilitre</InputOption>
              <InputOption value={"cl"}>cl - centilitre</InputOption>
              <InputOption value={"pt"}>pt - pint</InputOption>
              <InputOption value={"oz"}>oz - ounce</InputOption>
              <InputOption value={"g"}>g - gram</InputOption>
              <InputOption value={"kg"}>kg - kilogram</InputOption>
            </InputSelect>
          </InputContainer>

          <InputLabel>Quantity</InputLabel>
          <InputContainer>
            <InputNumber
              step={"1"}
              min={1}
              {...register("quantity", {
                required: true,
                valueAsNumber: true,
              })}
            />
          </InputContainer>

          <CustomerAdviseBanner control={control} />
        </DialogContent>
        <DialogCTA>
          <Button
            color={"red"}
            loading={caseSizeRuleDeleteMutation.isPending}
            onClick={() =>
              caseSizeRuleDeleteMutation.mutateAsync(undefined, {
                onSuccess: () => onDismiss(),
              })
            }
          >
            Delete
          </Button>
          <Button type="submit">Update</Button>
        </DialogCTA>
      </form>
    </Dialog>
  );
};
